import { useCallback, useState } from "react";
import { usePermissions } from "@/features/auth/hooks/usePermission";
import { triggerHaptic } from "@/utilities/haptics";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useTokenContext } from "@/features/betslip/hooks/useTokenContext";
import { useSemPrice } from "@/features/betslip/hooks/useSemPrice";
import { summariseBetPermissions } from "@/features/betslip/utils/permissions";
import { buildSrmFromSingles } from "@/features/betslip/builders/sem";
import { legSetKey } from "@/features/betslip/utils/rules";
import type { RaceSelection } from "@/features/betslip/types";

export type SrmSuggestion =
  | { kind: "none" }
  | {
      kind: "create";
      legs: RaceSelection[];
      // The combined SRM price, or null while it's being fetched.
      odds: number | null;
      createSrm: () => void;
      dismiss: () => void;
    };

export const useSrmSuggestion = (): SrmSuggestion => {
  const candidate = useBetslipStore((state) => state.srmCandidate);
  const addSelection = useBetslipStore((state) => state.addSelection);
  const permissions = usePermissions();
  const tokenContext = useTokenContext();
  const srmAllowed = summariseBetPermissions(permissions).srmAllowed;

  const legOutcomeIds = candidate
    ? candidate.legs.map((leg) => leg.outcomeId)
    : null;
  // Only price a structurally-valid set; the server settles whether this exact
  // SRM can be made now.
  const price = useSemPrice(candidate?.meetsCreateGates ? legOutcomeIds : null);

  const legKey = legOutcomeIds ? legSetKey(legOutcomeIds) : null;
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  const dismiss = useCallback(() => {
    if (legKey) setDismissedKey(legKey);
    triggerHaptic.click();
  }, [legKey]);

  const createSrm = useCallback(() => {
    if (!candidate || price.status !== "priced") return;
    triggerHaptic.click();
    const sem = buildSrmFromSingles(candidate.legs, price.odds);
    addSelection(sem, tokenContext);
  }, [candidate, price, addSelection, tokenContext]);

  if (!candidate || !srmAllowed) return { kind: "none" };
  if (legKey && legKey === dismissedKey) return { kind: "none" };

  // Show the card only when the SRM can actually be built: structurally valid
  // and priceable (or still pricing). A near-miss — position caps, too many
  // legs, a duplicate runner — or a set the server won't price shows nothing.
  if (!candidate.meetsCreateGates || price.status === "unpriceable") {
    return { kind: "none" };
  }

  return {
    kind: "create",
    legs: candidate.legs,
    odds: price.status === "priced" ? price.odds : null,
    createSrm,
    dismiss,
  };
};
